import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import type { ViewerLayout, ViewerLayoutDefault } from '../types'

export function useViewerLayouts(
  orgId: string | null | undefined,
  siteId?: string | null,
  groupName?: string | null,
) {
  const [layouts, setLayouts] = useState<ViewerLayout[]>([])
  const [defaults, setDefaults] = useState<ViewerLayoutDefault[]>([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    if (!orgId) { setLayouts([]); setDefaults([]); setLoading(false); return }
    setLoading(true)

    let query = supabase
      .from('viewer_layouts')
      .select('*')
      .order('name')

    // Filter by context — site viewer or group viewer
    if (siteId) {
      query = query.eq('site_id', siteId)
    } else if (groupName) {
      query = query.eq('group_name', groupName)
    }

    const { data } = await query
    setLayouts((data ?? []) as ViewerLayout[])

    // Load defaults for this site
    if (siteId) {
      const { data: defs } = await supabase
        .from('viewer_layout_defaults')
        .select('*')
        .eq('site_id', siteId)
      setDefaults((defs ?? []) as ViewerLayoutDefault[])
    }

    setLoading(false)
  }, [orgId, siteId, groupName])

  useEffect(() => { load() }, [load])

  const saveLayout = useCallback(async (layout: {
    name: string
    org_id: string
    created_by: string
    site_id?: string | null
    group_name?: string | null
    visibility: 'personal' | 'org'
    grid_size: string
    slots: (string | null)[]
  }) => {
    const { data, error } = await supabase
      .from('viewer_layouts')
      .insert(layout)
      .select()
      .single()
    if (error) throw error
    await load()
    return data as ViewerLayout
  }, [load])

  const updateLayout = useCallback(async (
    id: string,
    updates: Partial<Pick<ViewerLayout, 'name' | 'visibility' | 'grid_size' | 'slots'>>,
  ) => {
    const { error } = await supabase
      .from('viewer_layouts')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
    if (error) throw error
    await load()
  }, [load])

  const deleteLayout = useCallback(async (id: string) => {
    const { error } = await supabase
      .from('viewer_layouts')
      .delete()
      .eq('id', id)
    if (error) throw error
    await load()
  }, [load])

  // Set or clear a default for a site
  // scope: 'user' = personal default, 'org' = org-wide default
  const setDefault = useCallback(async (
    targetSiteId: string,
    layoutId: string,
    scope: 'user' | 'org',
    userId: string,
  ) => {
    if (scope === 'user') {
      // Upsert personal default
      const { error } = await supabase
        .from('viewer_layout_defaults')
        .upsert(
          { site_id: targetSiteId, layout_id: layoutId, user_id: userId },
          { onConflict: 'site_id,user_id' },
        )
      if (error) throw error
    } else {
      // Upsert org default (user_id = null)
      // Delete existing org default first then insert (partial unique index can't use onConflict)
      await supabase
        .from('viewer_layout_defaults')
        .delete()
        .eq('site_id', targetSiteId)
        .is('user_id', null)
      const { error } = await supabase
        .from('viewer_layout_defaults')
        .insert({ site_id: targetSiteId, layout_id: layoutId, user_id: null })
      if (error) throw error
    }
    await load()
  }, [load])

  const clearDefault = useCallback(async (
    targetSiteId: string,
    scope: 'user' | 'org',
    userId?: string,
  ) => {
    let query = supabase
      .from('viewer_layout_defaults')
      .delete()
      .eq('site_id', targetSiteId)

    if (scope === 'user' && userId) {
      query = query.eq('user_id', userId)
    } else {
      query = query.is('user_id', null)
    }

    const { error } = await query
    if (error) throw error
    await load()
  }, [load])

  // Resolve the effective default layout ID for the current user
  // Priority: user default > org default
  const getEffectiveDefault = useCallback((userId: string): string | null => {
    const userDefault = defaults.find(d => d.user_id === userId)
    if (userDefault) return userDefault.layout_id
    const orgDefault = defaults.find(d => d.user_id === null)
    if (orgDefault) return orgDefault.layout_id
    return null
  }, [defaults])

  return {
    layouts, defaults, loading,
    saveLayout, updateLayout, deleteLayout,
    setDefault, clearDefault, getEffectiveDefault,
  }
}
